import { TrashIcon } from '@heroicons/react/24/outline';
import React from 'react';

import { DiscriminatedTreatmentRequestDeletedEvent } from '@/services/api/generated/matterChangesService';
import { useGetMedicalProvidersQuery } from '@/services/api/treatmentService';

import {
  ActivityActions,
  ActivityBody,
  ActivityDescription,
  ActivityIcon,
  ActivityItem,
  ActivityLink,
  CollapsibleList,
} from '../activity-feed-v2';
import { getUserFromEvent } from '../activity-feed-v2/ActivityDiffList';
import { formatEnumValue } from '../activity-feed-v2/utils';

export interface TreatmentRequestDeletedEventProps {
  event: DiscriminatedTreatmentRequestDeletedEvent;
}

interface TreatmentRequestDetailField {
  label: string;
  value: string | number | boolean | null | undefined;
}

export const TreatmentRequestDeletedEvent: React.FC<
  TreatmentRequestDeletedEventProps
> = ({ event }) => {
  const { treatment_request } = event.payload;
  const user = getUserFromEvent(event);
  const { data: providers } = useGetMedicalProvidersQuery(event.matter_id);

  const provider = providers?.find(
    (p) => p.id === treatment_request.provider_id,
  );

  const icon = (
    <ActivityIcon
      icon={
        <div className="flex h-6 w-6 items-center justify-center rounded-full bg-red-100">
          <TrashIcon className="h-4 w-4 text-red-600" />
        </div>
      }
    />
  );

  const treatmentRequestDetailFields: TreatmentRequestDetailField[] = [
    { label: 'Provider', value: provider?.name },
    {
      label: 'Channel',
      value: treatment_request?.channel?.type
        ? formatEnumValue(treatment_request.channel.type)
        : null,
    },
    {
      label: 'Status',
      value: treatment_request?.status
        ? formatEnumValue(treatment_request.status)
        : null,
    },
  ];

  const detailItems = treatmentRequestDetailFields
    .filter(
      ({ value }) => value !== null && value !== undefined && value !== '',
    )
    .map(({ label, value }) => `${label}: ${value}`);

  const body = (
    <ActivityBody>
      <ActivityDescription>Records request deleted</ActivityDescription>
      {detailItems.length > 0 && (
        <CollapsibleList
          items={detailItems}
          showLabel="Show Details"
          hideLabel="Hide Details"
        />
      )}
    </ActivityBody>
  );

  const actions = event.payload.treatment_request?.provider_id && (
    <ActivityActions>
      <ActivityLink
        href={`/matters/${event.matter_id}/treatment/${event.payload.treatment_request.provider_id}`}
      >
        View Provider
      </ActivityLink>
    </ActivityActions>
  );

  return (
    <ActivityItem
      icon={icon}
      title="Records Request Deleted"
      timestamp={new Date(event.occurred_at ?? new Date())}
      user={user}
      body={body}
      actions={actions}
    />
  );
};
